import { formatPhoneDisplay } from '@/lib/contacts/phone';
import type { CustomerApprovalMethod } from '@/types/estimate';

/**
 * What the public estimate says when approval happens off the page.
 *
 * An estimate sent with an approval method of EMAIL or VERBAL carries no
 * Approve and no Decline button — the customer answers off-page and the
 * merchant records it. The page therefore has to hand the customer a way to
 * give that answer, and "please contact us" is not one: it names no address, no
 * number, and nothing to click.
 *
 * So the note leads with the channel the merchant chose and links it, falling
 * through to the other channel rather than to nothing: a customer who can reach
 * the business at all can approve, and the method is a preference about how,
 * not a vow of silence on every other line.
 *
 * Contact details are deliberately NOT gated on the design visibility flags that
 * govern the document header. `companyPhone` is false by default, so gating
 * would leave the default VERBAL estimate with no number to call — the exact
 * dead end this exists to remove. Those flags decide what is printed on the
 * estimate; this line is the customer's only route to answering it, and the
 * merchant asking to be reached on a channel outranks having tidied that
 * channel out of the letterhead. The estimate number stays gated, because it
 * rides along in the customer's sent mail and hiding it costs nothing here.
 */
export type OfflineApprovalNote = {
  /** Sentence up to the link, or the whole sentence when there is no link. */
  lead: string;
  link?: { href: string; label: string };
  /** Trailing punctuation after the link. */
  tail?: string;
};

export function offlineApprovalNote(args: {
  method: Extract<CustomerApprovalMethod, 'EMAIL' | 'VERBAL'>;
  companyEmail?: string | null;
  companyPhone?: string | null;
  estimateNumber?: string | null;
  /** `layoutVisibility(design).estimateNumber` — whether the number may be shown. */
  showEstimateNumber: boolean;
}): OfflineApprovalNote {
  const { method, companyEmail, companyPhone, estimateNumber, showEstimateNumber } = args;

  const email = companyEmail?.trim() || null;
  const phone = companyPhone?.trim() || null;

  const byEmail = (): OfflineApprovalNote | null => {
    if (!email) return null;
    const subject =
      showEstimateNumber && estimateNumber
        ? `Approve estimate ${estimateNumber}`
        : 'Approve my estimate';
    return {
      lead: 'To approve this estimate, email us at ',
      link: { href: `mailto:${email}?subject=${encodeURIComponent(subject)}`, label: email },
      tail: '.',
    };
  };

  const byPhone = (): OfflineApprovalNote | null => {
    if (!phone) return null;
    return {
      lead: 'To approve this estimate, call us at ',
      link: { href: `tel:${telHref(phone)}`, label: formatPhoneDisplay(phone) },
      tail: '.',
    };
  };

  const [preferred, alternate] = method === 'VERBAL' ? [byPhone, byEmail] : [byEmail, byPhone];

  return (
    preferred() ??
    alternate() ?? {
      // Last resort, and channel-neutral on purpose: an estimate can be sent by
      // SMS only, so "reply to the email we sent you" would be an instruction
      // the customer cannot follow. Whatever carried the estimate here is
      // something they can answer.
      lead: 'To approve this estimate, reply to the message we sent you.',
    }
  );
}

/**
 * `tel:` targets tolerate spaces and dashes but not the parentheses and dots
 * merchants type, so the href carries digits (and a leading +) only.
 */
function telHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  return digits.startsWith('+') ? `+${digits.slice(1).replace(/\+/g, '')}` : digits;
}
